
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QRCodeModal } from "@/components/tickets/QRCodeModal";
import { toast } from "sonner";
import {
  QrCode,
  Calendar,
  MapPin,
  Download,
  Share2,
  Ticket,
  Search
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";

type TicketData = {
  id: string;
  event: {
    id: string;
    name: string;
    start_time: string;
    venue_id: string;
    venue: {
      name: string;
      address: string;
    }
  };
  status: string;
  ticket_type: string;
  qr_code: string;
  scan_time: string | null;
  entry_status: string;
};

export const TicketsPage = () => {
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  // Fetch upcoming tickets
  const { data: upcomingTickets, isLoading: isUpcomingLoading } = useQuery({
    queryKey: ["upcomingTickets"],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      
      if (!user.user) {
        throw new Error("Not authenticated");
      }
      
      const now = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('tickets')
        .select(`
          id,
          status,
          ticket_type,
          qr_code,
          scan_time,
          entry_status,
          event:event_id (
            id,
            name,
            start_time,
            venue_id,
            venue:venue_id (
              name,
              address
            )
          )
        `)
        .eq('patron_id', user.user.id)
        .eq('status', 'valid')
        .gte('event.start_time', now)
        .order('event.start_time', { ascending: true });
      
      if (error) {
        throw error;
      }
      
      return data as TicketData[];
    },
  });

  // Fetch past tickets
  const { data: pastTickets, isLoading: isPastLoading } = useQuery({
    queryKey: ["pastTickets"],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      
      if (!user.user) {
        throw new Error("Not authenticated");
      }
      
      const now = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('tickets')
        .select(`
          id,
          status,
          ticket_type,
          qr_code,
          scan_time,
          entry_status,
          event:event_id (
            id,
            name,
            start_time,
            venue_id,
            venue:venue_id (
              name,
              address
            )
          )
        `)
        .eq('patron_id', user.user.id)
        .or(`status.eq.valid,status.eq.checked_in`)
        .lt('event.start_time', now)
        .order('event.start_time', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      return data as TicketData[];
    },
  });

  const handleShare = async (ticket: TicketData) => {
    try {
      const shareData = {
        title: ticket.event.name,
        text: `Join me at ${ticket.event.name} at ${ticket.event.venue.name} on ${new Date(ticket.event.start_time).toLocaleDateString()}!`,
        url: `${window.location.origin}/dashboard/tickets`
      };

      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(
          `${shareData.text} ${shareData.url}`
        );
        toast.success("Ticket details copied to clipboard!");
      }
    } catch (error) {
      toast.error("Failed to share ticket");
    }
  };

  const handleDownload = (ticket: TicketData) => {
    // In a real app, this would generate and download a PDF ticket
    toast.success("Ticket download started");
  };

  if (isUpcomingLoading || isPastLoading) {
    return <div className="flex justify-center py-12">Loading tickets...</div>;
  }

  // Empty state when no tickets are found
  if ((!upcomingTickets || upcomingTickets.length === 0) && 
      (!pastTickets || pastTickets.length === 0)) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">My Tickets</h1>
        
        <div className="flex flex-col items-center justify-center py-16 px-4 border-2 border-dashed border-gray-200 rounded-lg">
          <Ticket className="h-16 w-16 text-gray-300 mb-4" />
          <h3 className="text-xl font-medium text-gray-800 mb-2">No tickets found</h3>
          <p className="text-gray-500 text-center max-w-md mb-6">
            You haven't purchased any tickets yet. Browse upcoming events and get tickets to see them here.
          </p>
          <Button asChild>
            <Link to="/dashboard/events">
              <Search className="h-4 w-4 mr-2" />
              Find Events
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">My Tickets</h1>

      {upcomingTickets && upcomingTickets.length > 0 && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Upcoming Events</h2>
          <div className="space-y-4">
            {upcomingTickets.map((ticket) => {
              const eventDate = new Date(ticket.event.start_time).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              });
              const eventTime = new Date(ticket.event.start_time).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit'
              });
              
              return (
                <div key={ticket.id} className="border rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold">{ticket.event.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <Calendar className="h-4 w-4" />
                        {eventDate} at {eventTime}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <MapPin className="h-4 w-4" />
                        {ticket.event.venue.name}, {ticket.event.venue.address}
                      </div>
                    </div>
                    <Badge variant="default">{ticket.status === 'checked_in' ? 'Used' : 'Valid'}</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="space-y-1">
                      <Badge variant="secondary">{ticket.ticket_type}</Badge>
                      {ticket.entry_status !== 'not_entered' && (
                        <p className="text-sm text-muted-foreground">
                          Entered: {ticket.scan_time ? new Date(ticket.scan_time).toLocaleString() : 'N/A'}
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground">Ticket #{ticket.id.substring(0, 8)}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedTicketId(ticket.id)}
                      >
                        <QrCode className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDownload(ticket)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleShare(ticket)}
                      >
                        <Share2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {pastTickets && pastTickets.length > 0 && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Past Events</h2>
          <div className="space-y-4">
            {pastTickets.map((ticket) => {
              const eventDate = new Date(ticket.event.start_time).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              });
              
              return (
                <div key={ticket.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-medium">{ticket.event.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      {eventDate}
                      <MapPin className="h-4 w-4 ml-2" />
                      {ticket.event.venue.name}
                    </div>
                    <Badge variant="secondary" className="mt-2">
                      {ticket.ticket_type}
                    </Badge>
                  </div>
                  <Badge variant="outline">{ticket.status === 'checked_in' ? 'Used' : ticket.status}</Badge>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <QRCodeModal 
        isOpen={!!selectedTicketId}
        onClose={() => setSelectedTicketId(null)}
        ticketId={selectedTicketId || ""}
      />
    </div>
  );
};
